import { useCallback } from 'react';
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

const STORAGE_KEY = 'accio3d.agent.thread';

interface StoredThreadState {
  /* The conversation to reopen, tagged with the org it belongs to. */
  stored: { organizationId: string; threadId: string } | null;
}

const useStore = create<StoredThreadState>()(
  persist(() => ({ stored: null }), {
    name: STORAGE_KEY,
    storage: createJSONStorage(() => sessionStorage),
  }),
);

export function setStoredThreadId(organizationId: string, threadId: string | undefined): void {
  useStore.setState({ stored: threadId ? { organizationId, threadId } : null });
}

/* The remembered thread for this org, or undefined  */
export function storedThreadIdFor(organizationId: string | undefined): string | undefined {
  const { stored } = useStore.getState();
  return stored && organizationId && stored.organizationId === organizationId
    ? stored.threadId
    : undefined;
}

interface StoredThread {
  threadId: string | undefined;
  setThreadId: (next: string | undefined) => void;
}

export function useStoredThread(organizationId: string | undefined): StoredThread {
  const stored = useStore((state) => state.stored);

  const setThreadId = useCallback(
    (next: string | undefined) => {
      if (!organizationId) return;
      setStoredThreadId(organizationId, next);
    },
    [organizationId],
  );

  return {
    threadId:
      stored && organizationId && stored.organizationId === organizationId
        ? stored.threadId
        : undefined,
    setThreadId,
  };
}

export function resetStoredThread(): void {
  useStore.persist.clearStorage();
  useStore.setState({ stored: null });
}
